import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock3, ArrowRight, Building2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { SchemaJsonLd, localBusinessSchema } from "@/lib/seo";
import { CONTACT } from "@/lib/siteData";

export default function ContactPage() {
  const url = `${SEO_DEFAULTS.baseUrl}/contact`;
  useSEO({
    title: "Contact | Aware Water Testing — Noosaville Laboratory",
    description:
      "Get in touch with Aware Water Testing. Call (07) 5327 3473 or email lab@awarewatertesting.com.au. Independent water testing laboratory based in Noosaville.",
    url,
  });

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SchemaJsonLd data={localBusinessSchema()} id="jsonld-localbusiness" />

      <Header />

      <main>
        {/* HERO */}
        <section className="pt-28 lg:pt-36 pb-12 lg:pb-16 bg-gradient-to-br from-slate-50 via-white to-cyan-50/40 border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumbs items={[{ name: "Contact" }]} />

            <div className="mt-6 max-w-3xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight">
                Get in touch with the lab
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Local water testing laboratory in Noosaville. Call us, email us, or drop your sample to the lab. Whether you have a question about a test, need help choosing the right package, or want to book in a property — we're here to help.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/book-water-test"
                  data-testid="contact-book-btn"
                  className="btn-aqua inline-flex items-center gap-1.5 px-6 py-3 rounded-full text-sm font-semibold"
                >
                  Book a water test <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/property-managers"
                  data-testid="contact-pm-link"
                  className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full text-sm font-semibold border border-slate-200 hover:border-cyan-400 hover:text-cyan-700 transition-colors"
                >
                  For property managers
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT CARDS */}
        <section className="py-14 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
              <a
                href={CONTACT.phoneLink}
                data-testid="contact-phone-card"
                className="group block rounded-2xl bg-white border border-slate-200 p-6 lg:p-8 hover:border-cyan-300 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center mb-5">
                  <Phone className="w-6 h-6" />
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Phone</div>
                <div className="text-2xl font-display font-bold text-slate-900 tabular-nums">{CONTACT.phone}</div>
                <p className="mt-3 text-sm text-slate-600">Speak with the lab — best for urgent questions, settlement-deadline bookings, and trade enquiries.</p>
              </a>

              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-card"
                className="group block rounded-2xl bg-white border border-slate-200 p-6 lg:p-8 hover:border-cyan-300 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center mb-5">
                  <Mail className="w-6 h-6" />
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Email</div>
                <div className="text-base lg:text-lg font-display font-semibold text-slate-900 break-all">{CONTACT.email}</div>
                <p className="mt-3 text-sm text-slate-600">For test booking questions, results follow-up, or to attach photos / supporting docs.</p>
              </a>

              <div className="md:col-span-2 rounded-2xl bg-slate-900 text-white p-6 lg:p-8">
                <div className="grid sm:grid-cols-3 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3 text-cyan-400">
                      <MapPin className="w-5 h-5" />
                      <span className="text-xs uppercase tracking-wider font-bold">Lab address</span>
                    </div>
                    <p className="text-base font-semibold">{CONTACT.address}</p>
                    <p className="text-xs text-slate-400 mt-2">Sample drop-off welcome during business hours</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-3 text-cyan-400">
                      <Clock3 className="w-5 h-5" />
                      <span className="text-xs uppercase tracking-wider font-bold">Hours</span>
                    </div>
                    <p className="text-base font-semibold">Mon – Fri</p>
                    <p className="text-sm text-slate-300">8:00 am – 4:30 pm</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-3 text-cyan-400">
                      <Building2 className="w-5 h-5" />
                      <span className="text-xs uppercase tracking-wider font-bold">For trade</span>
                    </div>
                    <p className="text-base font-semibold">Property managers &amp; real estate</p>
                    <Link to="/property-managers" className="text-sm text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1 mt-2">
                      Set up an agency account <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* QUICK LINKS */}
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { to: "/aware-4-point", label: "AWARE 4-Point" },
                { to: "/aware-tank-360", label: "AWARE Tank 360" },
                { to: "/aware-bore-360", label: "AWARE Bore 360" },
                { to: "/locations", label: "Service Areas" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="block rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-cyan-300 hover:shadow transition-all px-4 py-3 text-sm font-semibold text-slate-800"
                >
                  {l.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
